import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import {
  useSubscriptionStore,
  useCurrentPlan,
  SUBSCRIPTION_PLANS,
} from '@/stores/subscriptionStore';
import type { SubscriptionTier } from '@/types';
import type {
  SubscriptionPlan,
  FeatureName,
  LimitName,
  FeatureCheckResult,
  LimitCheckResult,
  UsageMetrics,
  SubscriptionStatus,
  BillingInfo,
} from '@/types/subscription';

// ============================================================================
// CONTEXT TYPE
// ============================================================================

interface SubscriptionContextValue {
  // Current subscription info
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  plan: SubscriptionPlan;
  allPlans: SubscriptionPlan[];

  // Trial info
  isTrialing: boolean;
  trialDaysRemaining: number;

  // Demo mode (bypasses restrictions)
  isDemoMode: boolean;

  // Billing
  billing: BillingInfo | null;

  // Usage
  usage: UsageMetrics;

  // Feature checks
  checkFeature: (feature: FeatureName) => FeatureCheckResult;
  hasFeature: (feature: FeatureName) => boolean;

  // Limit checks
  checkLimit: (limit: LimitName, currentValue?: number) => LimitCheckResult;
  isWithinLimit: (limit: LimitName, currentValue?: number) => boolean;

  // Plan comparison
  canUpgradeTo: (tier: SubscriptionTier) => boolean;
  getPlanByTier: (tier: SubscriptionTier) => SubscriptionPlan;

  // Actions
  setTier: (tier: SubscriptionTier) => void;
  incrementWorkOrders: () => void;
}

// ============================================================================
// CONTEXT
// ============================================================================

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null);

// ============================================================================
// PROVIDER
// ============================================================================

interface SubscriptionProviderProps {
  children: ReactNode;
}

export function SubscriptionProvider({ children }: SubscriptionProviderProps) {
  const store = useSubscriptionStore();
  const plan = useCurrentPlan();

  const value = useMemo<SubscriptionContextValue>(
    () => ({
      // Current subscription
      tier: store.tier,
      status: store.status,
      plan,
      allPlans: SUBSCRIPTION_PLANS,

      // Trial
      isTrialing: store.isTrialing,
      trialDaysRemaining: store.getTrialDaysRemaining(),

      // Demo
      isDemoMode: store.isDemoMode,

      // Billing
      billing: store.billing,

      // Usage
      usage: store.usage,

      // Feature checks
      checkFeature: store.checkFeature,
      hasFeature: store.hasFeature,

      // Limit checks
      checkLimit: store.checkLimit,
      isWithinLimit: store.isWithinLimit,

      // Plan comparison
      canUpgradeTo: store.canUpgradeTo,
      getPlanByTier: store.getPlanByTier,

      // Actions
      setTier: store.setTier,
      incrementWorkOrders: store.incrementWorkOrders,
    }),
    [store, plan]
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

// ============================================================================
// HOOKS
// ============================================================================

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}

// Convenience hooks for common checks
export function useFeature(feature: FeatureName): FeatureCheckResult {
  const { checkFeature } = useSubscription();
  return checkFeature(feature);
}

export function useHasFeature(feature: FeatureName): boolean {
  const { hasFeature } = useSubscription();
  return hasFeature(feature);
}

export function useLimit(limit: LimitName, currentValue?: number): LimitCheckResult {
  const { checkLimit } = useSubscription();
  return checkLimit(limit, currentValue);
}

export function useIsWithinLimit(limit: LimitName, currentValue?: number): boolean {
  const { isWithinLimit } = useSubscription();
  return isWithinLimit(limit, currentValue);
}

export function useTrialStatus() {
  const { isTrialing, trialDaysRemaining } = useSubscription();
  return { isTrialing, trialDaysRemaining };
}

export function useIsPaidTier(): boolean {
  const { tier } = useSubscription();
  return tier !== 'free';
}

export function useCanAccessIoT(): boolean {
  const { hasFeature } = useSubscription();
  return hasFeature('iotMonitoring');
}
